// src/services/cleanupScheduler.js
import { clearAllOldActivities } from "./activityService";
import { toast } from "react-toastify";

const CLEANUP_STORAGE_KEY = "lastActivityCleanup";
const CLEANUP_INTERVAL_DAYS = 7; // Run cleanup weekly

export class CleanupScheduler {
  static async shouldRunCleanup() {
    const lastCleanup = localStorage.getItem(CLEANUP_STORAGE_KEY);

    if (!lastCleanup) {
      return true; // First time, should run cleanup
    }

    const lastCleanupDate = new Date(lastCleanup);
    const now = new Date();
    const daysSinceLastCleanup = Math.floor(
      (now - lastCleanupDate) / (1000 * 60 * 60 * 24)
    );

    return daysSinceLastCleanup >= CLEANUP_INTERVAL_DAYS;
  }

  static async runScheduledCleanup() {
    try {
      console.log("🧹 Starting scheduled activity cleanup...");

      const result = await clearAllOldActivities();

      // Update last cleanup timestamp
      localStorage.setItem(CLEANUP_STORAGE_KEY, new Date().toISOString());

      console.log(`✅ Cleanup completed: ${result.message}`);

      // Only show toast if activities were actually deleted
      if (result.deletedCount > 0) {
        toast.success(`Cleaned up ${result.deletedCount} old activities`, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }

      return result;
    } catch (error) {
      console.error("❌ Scheduled cleanup failed:", error);
      toast.error("Activity cleanup failed", {
        position: "bottom-right",
        autoClose: 3000,
      });
      throw error;
    }
  }

  static async initializeScheduler() {
    try {
      // Check if cleanup should run
      const shouldRun = await this.shouldRunCleanup();

      if (shouldRun) {
        console.log("🕐 Initializing scheduled cleanup...");
        await this.runScheduledCleanup();
      } else {
        const lastCleanup = localStorage.getItem(CLEANUP_STORAGE_KEY);
        const nextCleanup = new Date(lastCleanup);
        nextCleanup.setDate(nextCleanup.getDate() + CLEANUP_INTERVAL_DAYS);
        console.log(
          `⏰ Next cleanup scheduled for: ${nextCleanup.toLocaleDateString()}`
        );
      }
    } catch (error) {
      console.error("Failed to initialize cleanup scheduler:", error);
    }
  }

  static async forceCleanup() {
    console.log("🔧 Force running cleanup...");
    return await this.runScheduledCleanup();
  }

  static getLastCleanupDate() {
    const lastCleanup = localStorage.getItem(CLEANUP_STORAGE_KEY);
    return lastCleanup ? new Date(lastCleanup) : null;
  }

  static getNextCleanupDate() {
    const lastCleanup = this.getLastCleanupDate();
    if (!lastCleanup) return null;

    const nextCleanup = new Date(lastCleanup);
    nextCleanup.setDate(nextCleanup.getDate() + CLEANUP_INTERVAL_DAYS);
    return nextCleanup;
  }

  static getDaysUntilNextCleanup() {
    const nextCleanup = this.getNextCleanupDate();
    if (!nextCleanup) return 0;

    const now = new Date();
    const daysUntil = Math.ceil((nextCleanup - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysUntil);
  }
}

// Auto-initialize when module is imported
if (typeof window !== "undefined") {
  // Only run in browser environment
  setTimeout(() => {
    CleanupScheduler.initializeScheduler();
  }, 2000); // Delay to allow app to fully load
}
